import { DeleteOutlined, EditOutlined, MenuOutlined } from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Form, Input, List, Switch } from 'antd';
import type { FormListFieldData } from 'antd';
import type { FC } from 'react';
import { useCallback, useState } from 'react';

import { EmojiModal } from 'app/components/commons/Package/Headlines/EmojiModal/EmojiModal';
import { PictureComponent } from 'app/components/commons/PictureComponent/PictureComponent';
import type { Picture } from 'app/components/commons/Uploader/Types';

import './Headline.scss';

const RULES = [{ required: true, message: 'Required' }];

export const Headline: FC<IProps> = ({
  field,
  mode,
  remove,
  fieldIndex,
  values,
  setFieldsValue,
  dndId,
  disabled,
}) => {
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: dndId ?? field.key, disabled: !!disabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const renderAvatar = () => (
    <Form.Item
      name={[field.name, 'emoji']}
      rules={RULES}
      valuePropName="key"
      noStyle
    >
      <div
        onClick={
          mode === 'edit' && !values?.headlines?.[fieldIndex].experienceId
            ? () => setIsEmojiOpen(true)
            : undefined
        }
        className={`headline__avatar ${
          mode === 'edit' && !values?.headlines?.[fieldIndex].experienceId
            ? 'headline__avatar-clickable'
            : ''
        }`}
        key={values?.headlines?.[fieldIndex].emoji?.pictureId}
      >
        {values?.headlines?.[fieldIndex].emoji ? (
          <PictureComponent
            {...values.headlines[fieldIndex].emoji}
            width={40}
          />
        ) : (
          <EditOutlined />
        )}
      </div>
    </Form.Item>
  );

  const renderTitle = () =>
    values?.headlines?.[fieldIndex].experienceId
      ? values.headlines[fieldIndex].title
      : 'Custom headline';

  const renderDescription = () =>
    mode === 'view' ? (
      <div className="headline__description-view-container">
        <div className="headline__description-view headline__description-view-no-input">
          {values?.headlines?.[fieldIndex].valueProposition}
        </div>
      </div>
    ) : (
      <div className="headline__description-view-container">
        <Form.Item
          name={[field.name, 'valueProposition']}
          rules={values?.headlines?.[fieldIndex].featured ? RULES : undefined}
          noStyle
        >
          <Input className="headline__description-view" />
        </Form.Item>
        {!values?.headlines?.[fieldIndex].experienceId && (
          <DeleteOutlined onClick={() => remove(fieldIndex)} />
        )}
      </div>
    );

  const renderSwitch = () => (
    <Form.Item name={[field.name, 'featured']} valuePropName="checked" noStyle>
      <Switch size="small" />
    </Form.Item>
  );

  const renderDragHandler = () => (
    <MenuOutlined
      key="handle"
      className="headline__drag-handle"
      style={{ cursor: 'grab' }}
      {...attributes}
      {...listeners}
    />
  );

  const onEmojiSelect = useCallback(
    (emoji: Picture) => {
      if (!values?.headlines) {
        return;
      }

      setFieldsValue({
        ...values,
        headlines: values.headlines.map((h, idx) =>
          idx === fieldIndex ? { ...h, emoji } : h
        ),
      });
      setIsEmojiOpen(false);
    },
    [values, setFieldsValue, fieldIndex]
  );

  return (
    <List.Item
      ref={setNodeRef}
      style={style}
      className="headline"
      actions={mode === 'edit' ? [renderSwitch(), renderDragHandler()] : []}
    >
      <List.Item.Meta
        avatar={renderAvatar()}
        title={renderTitle()}
        description={renderDescription()}
      />
      <EmojiModal
        visible={isEmojiOpen}
        onSelect={onEmojiSelect}
        onCancel={() => setIsEmojiOpen(false)}
      />
    </List.Item>
  );
};

type HeadlineForm = {
  headlines?: {
    emoji: Picture;
    valueProposition: string;
    experienceId: number;
    featured: boolean;
    title?: string;
  }[];
};

type IProps = {
  field: FormListFieldData;
  mode: string;
  remove: (index: number) => void;
  fieldIndex: number;
  values?: HeadlineForm;
  setFieldsValue: (values: HeadlineForm) => void;
  dndId?: number;
  disabled?: boolean;
};
